import { useMemo } from 'react';

import path from 'path';

import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import ErrorPage from '~components/errorBoundary/errorPage';
import ErrorType from '~components/errorBoundary/errorType';
import { pagePathSegments, queryParameters } from '~constants';
import { UserRoleEnum } from '~enums';
import { useSession } from '~hooks';
import useAuthorize from '~hooks/useAuthorize';
import PageLayout from '~layouts/page/pageLayout';
import { getService } from '~services/serviceFactory';

import UserDetailsContainer from '../containers/userDetailsContainer';
import UserProvider from '../providers/userProvider';
import UsersService from '../services/usersService';

interface UserDetailsPageProps {
	showCurrentUser?: boolean;
}

const usersService = getService(UsersService);

function UserDetailsPage({ showCurrentUser = false }: UserDetailsPageProps) {
	const { t } = useTranslation('entities');
	const { user } = useSession();
	const auth = useAuthorize();
	const params = useParams<{ id?: string }>();
	const [searchParams] = useSearchParams();

	const overwrittenClientId = searchParams.get(queryParameters.OverwrittenClientId) ?? undefined;
	const userId = showCurrentUser ? user?.id : params.id;

	const cacheKey = usersService.getUserByIdCacheKey(
		userId === user?.id ? 'me' : (userId ?? ''),
		overwrittenClientId
	);
	const {
		data: userData,
		error: userError,
		isLoading,
	} = useSWR(
		cacheKey,
		([_, id, organisationId]) =>
			showCurrentUser ? usersService.getCurrentUser()
			: id ?
				usersService.getUserById({ userId: id, overwrittenClientId: organisationId ?? undefined })
			:	null,
		{
			revalidateOnMount: true,
		}
	);

	// Only admins and super admins
	// Can't change your own role
	// Can't edit super admin roles
	const allowOperations =
		auth.authorize([UserRoleEnum.ClientAdmin, UserRoleEnum.SuperAdmin]) &&
		userId !== user?.id &&
		userData?.role != UserRoleEnum.SuperAdmin;

	const crumbs = useMemo(
		() => [
			{
				title: t('user.labels.plural', { ns: 'entities' }),
				path: path.join('/', pagePathSegments.Users),
			},
			{
				title: user ? `${userData?.firstName} ${userData?.lastName}` : (userId ?? ''),
				path: '',
			},
		],
		[userData]
	);

	const computedTitle = useMemo(
		() =>
			userData ?
				`${userData?.firstName} ${userData?.lastName}`
			:	`${t('user.labels.single')} ${userId ?? ''}`,
		[userData]
	);

	// --- All hooks above, all early returns below ---
	if (userId == undefined) {
		return <Navigate to={pagePathSegments.Users} replace />;
	}

	if (!isLoading && userError) {
		return <ErrorPage type={ErrorType.NotFound} />;
	}

	return (
		<UserProvider value={{ isLoading: isLoading, user: userData ?? undefined }}>
			<PageLayout
				pageTitle={computedTitle}
				crumbs={crumbs}
				isLoading={isLoading}
				headerInfo={{
					title: computedTitle,
				}}
			>
				<UserDetailsContainer
					userId={userId}
					organisationId={overwrittenClientId}
					allowRoleEditing={allowOperations}
					allowDeleting={allowOperations}
				/>
			</PageLayout>
		</UserProvider>
	);
}

export default UserDetailsPage;
